'use client'

import { Clock24, PlusSquareOutlined24, Truck24, X24 } from '@vinted/monochrome-icons'
import { Badge, Button, Cell, Divider, Icon, Image, Navigation, Spacer, Text } from '@vinted/web-ui'
import { noop } from 'lodash'
import { Fragment, useEffect, useState } from 'react'

import { AccountRestrictionAction } from '@marketplace-web/user-restrictions/user-restrictions-data'
import { ModeratedItemCell } from '@marketplace-web/moderation/conversation-moderation-feature'
import {
  CarrierChangeModal,
  ExtendShippingDeadlineModal,
} from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { StartBundleButtonWrapper } from '@marketplace-web/bundles/items-bundle-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { urlWithParams } from '@marketplace-web/shared/url-util'
import { OrderActions } from '@marketplace-web/item-seller-action-buttons/conversation-order-actions-feature'
import {
  getSnadComplaint,
  transformComplaintDto,
} from '@marketplace-web/complaints/complaints-data'
import {
  AdditionalTransactionAction,
  conversationDetailsItemClick,
  conversationDetailsUserNameClick,
  TransactionItemModel,
  clickEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ConversationDetailsMemberProtectionPolicies } from '@marketplace-web/seller-protection/seller-protection-feature'

import { CREATE_BUNDLE_URL, MEMBER_PROFILE_URL } from '../../constants/routes'
import { ConversationActions } from '../ConversationActions'
import { getConversationIdFromUrl } from '../../utils'
import { isDateInTheFuture } from '../../utils/date'

type Props = {
  toggleSidebar?: () => void
}

const ConversationDetails = ({ toggleSidebar = noop }: Props) => {
  const { relativeUrl } = useBrowserNavigation()
  const { transaction, conversation } = useConversationContext()
  const { user } = useSession()

  const noPhotoAsset = useAsset('assets/no-photo')
  const translate = useTranslate()
  const { track } = useTracking()
  const inboxTranslate = useTranslate('inbox')
  const infoTranslate = useTranslate('conversation.conversation_info')
  const [isExtendShippingDeadlineModalOpen, setIsExtendShippingDeadlineModalOpen] = useState(false)
  const [isCarrierChangeModalOpen, setIsCarrierChangeModalOpen] = useState(false)

  const isSeller = transaction?.userSide === 'seller'
  const isSellerPreShippedOrderActionsVisible =
    isSeller &&
    (!!transaction?.availableActions.includes(AdditionalTransactionAction.ExtendShippingDeadline) ||
      !!transaction?.availableActions.includes(AdditionalTransactionAction.ChangeDeliveryOption))
  const sellerConversationDetailsGroupOrderActionsABTest = useAbTest(
    'seller_conversation_details_group_order_actions',
  )
  const isSellerConversationDetailsGroupOrderActionsAbTestOn =
    sellerConversationDetailsGroupOrderActionsABTest?.variant === 'on' && isSeller
  useTrackAbTest(
    sellerConversationDetailsGroupOrderActionsABTest,
    isSellerPreShippedOrderActionsVisible,
  )

  const oppositeUser = conversation?.oppositeUser
  const moderatedItems = conversation?.moderatedItems
  const transactionId = conversation?.transactionId
  const conversationId = conversation?.id
  const complaintId = transaction?.complaintId

  const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

  const {
    fetch: fetchSnadComplaint,
    transformedData: complaint,
    isLoading,
  } = useFetch(getSnadComplaint, transformComplaintDto)

  useEffect(() => {
    if (!complaintId || !transactionId) return

    fetchSnadComplaint(transactionId)
  }, [complaintId, transactionId, fetchSnadComplaint])

  const trackExtendShippingDeadlineClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'extend_shipping_deadline',
        screen: 'order_details',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackCarrierChangeClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'request_carrier_change',
        screen: 'order_details',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleExtendShippingDeadlineClick = () => {
    trackExtendShippingDeadlineClick()
    setIsExtendShippingDeadlineModalOpen(true)
  }

  const handleExtendShippingDeadlineModalClose = () => {
    setIsExtendShippingDeadlineModalOpen(false)
  }

  const handleCarrierChangeClick = () => {
    trackCarrierChangeClick()
    setIsCarrierChangeModalOpen(true)
  }

  const handleCarrierChangeModalClose = () => {
    setIsCarrierChangeModalOpen(false)
  }

  const handleCarrierChangeModalSubmit = () => {
    toggleSidebar()
  }

  const renderAvailableDiscount = () => {
    const availableDiscount = transaction?.seller?.bundleDiscount

    if (!availableDiscount) return translate('bundles.discounts.not_available')

    const { discounts } = availableDiscount

    if (!discounts || !availableDiscount.isEnabled) {
      return translate('bundles.discounts.not_available')
    }

    const maxDiscount = Math.max(...discounts.map(element => Number(element.fraction))) * 100
    const maxItems = Math.max(...discounts.map(element => element.minimalItemCount))

    return translate('bundles.discounts.available', {
      availableDiscount: maxDiscount,
      count: maxItems,
    })
  }

  const renderAddMoreItemsAction = () => {
    if (!transaction?.seller || isSellerCountdownActive) return null

    const addMoreBundleItemsUrl = urlWithParams(CREATE_BUNDLE_URL(transaction.seller.id), {
      transaction_id: transaction.id,
      item_ids: transaction.order.itemIds,
    })

    return (
      <StartBundleButtonWrapper url={addMoreBundleItemsUrl} screen="order_details">
        {({ onClick }) => (
          <Cell
            chevron
            title={infoTranslate('edit_order')}
            body={renderAvailableDiscount()}
            prefix={<Icon name={PlusSquareOutlined24} color={Icon.Color.Primary} />}
            type={Cell.Type.Navigating}
            onClick={onClick}
            testId="conversation-info-add-more-items-cell"
          />
        )}
      </StartBundleButtonWrapper>
    )
  }

  const renderOrderItemDetails = (item: TransactionItemModel) => {
    const complaintOrderItem = complaint?.complaintOrderItems?.find(
      complaintItem => complaintItem.item.id === item.id,
    )

    if (!complaintOrderItem?.reasonText) return null

    return (
      <Badge
        content={complaintOrderItem.reasonText}
        theme="warning"
        styling={Badge.Styling.Light}
      />
    )
  }

  const handleOrderItemClick = () => {
    if (!transactionId || !conversationId) return

    track(
      conversationDetailsItemClick({
        transactionId,
        conversationId: String(conversationId),
        userSide: transaction?.userSide ?? null,
        oppositeUserId: oppositeUser?.id ?? null,
      }),
    )
  }

  const renderOrderItems = () => {
    if (!transaction) return null

    if (complaintId && isLoading) return <ContentLoader testId="conversation-info-loader" />

    const {
      order: { items },
      availableActions,
    } = transaction

    return (
      <>
        {items?.map(item => (
          <Fragment key={item.id}>
            <Cell
              type={Cell.Type.Navigating}
              chevron
              title={item.title}
              body={renderOrderItemDetails(item)}
              onClick={handleOrderItemClick}
              testId="conversation-item"
              prefix={
                <Image
                  src={item.thumbnailUrl}
                  size={Image.Size.Large}
                  styling={Image.Styling.Rounded}
                />
              }
              url={item.url}
            />
            <Divider />
          </Fragment>
        ))}
        {availableActions?.includes(AdditionalTransactionAction.Bundle) &&
          renderAddMoreItemsAction()}
      </>
    )
  }

  const renderOrderActions = () => {
    const isSellingRestricted =
      user?.action_restriction?.action === AccountRestrictionAction.Selling

    return (
      <OrderActions
        transaction={transaction}
        oppositeUser={oppositeUser}
        isSellingRestricted={isSellingRestricted}
      />
    )
  }

  const handleUsernameClick = () => {
    if (!conversationId || !transactionId) return

    track(
      conversationDetailsUserNameClick({
        conversationId: String(conversationId),
        transactionId,
        userSide: transaction?.userSide ?? null,
        oppositeUserId: oppositeUser?.id ?? null,
      }),
    )
  }

  const renderMemberProtectionPolicies = () => (
    <ConversationDetailsMemberProtectionPolicies
      transactionId={transactionId}
      userSide={transaction?.userSide}
      isSellerBusiness={!!transaction?.seller?.business}
      transactionStatus={transaction?.status}
      shipmentStatus={transaction?.shipment?.status}
    />
  )

  const renderOppositeUser = () => {
    if (!oppositeUser) return null

    const { login, id, photoThumbnail, isSystem } = oppositeUser

    const getImageSrc = () => {
      if (isSystem) return noPhotoAsset('user-clothing/system-photo/default.png')
      if (photoThumbnail) return photoThumbnail

      return noPhotoAsset(EMPTY_USER_IMAGE_NAME)
    }

    return (
      <>
        <Cell
          title={login}
          prefix={
            <Image
              role="img"
              src={getImageSrc()}
              size={Image.Size.Medium}
              styling={Image.Styling.Circle}
              testId="opposite-user-image"
            />
          }
          url={MEMBER_PROFILE_URL(id)}
          onClick={handleUsernameClick}
          type={Cell.Type.Navigating}
        />
        <Divider />
      </>
    )
  }

  const renderConversationActions = () => {
    return (
      <ConversationActions
        transaction={transaction}
        conversationId={getConversationIdFromUrl(relativeUrl)}
        oppositeUser={oppositeUser}
        userId={user?.id}
      />
    )
  }

  const renderExtendShippingDeadlineAction = (isVisible: boolean) => {
    if (!isVisible) return null

    const isExtendDeadlineActionExist = transaction?.availableActions.includes(
      AdditionalTransactionAction.ExtendShippingDeadline,
    )

    if (!isExtendDeadlineActionExist || !transaction) return null

    return (
      <>
        <Cell
          title={infoTranslate('extend_shipping_deadline')}
          prefix={<Icon name={Clock24} color={Icon.Color.Primary} />}
          type={Cell.Type.Navigating}
          testId="conversation-info-extend-shipping-deadline"
          onClick={handleExtendShippingDeadlineClick}
        />
        {!isSellerConversationDetailsGroupOrderActionsAbTestOn && <Divider />}
      </>
    )
  }

  const renderChangeDeliveryOptionAction = (isVisible: boolean) => {
    if (!isVisible) return null

    const isChangeDeliveryOptionExist = transaction?.availableActions.includes(
      AdditionalTransactionAction.ChangeDeliveryOption,
    )

    if (!isChangeDeliveryOptionExist || !transaction) return null

    return (
      <>
        <Cell
          title={infoTranslate('change_delivery_option')}
          prefix={<Icon name={Truck24} color={Icon.Color.Primary} />}
          type={Cell.Type.Navigating}
          testId="conversation-info-change-delivery-option"
          onClick={handleCarrierChangeClick}
        />
        {!isSellerConversationDetailsGroupOrderActionsAbTestOn && <Divider />}
      </>
    )
  }

  const renderModeratedItems = () => {
    if (!moderatedItems) return null

    return (
      <>
        <SeparatedList separator={<Divider />}>
          {moderatedItems.items.map(item => (
            <ModeratedItemCell item={item} key={item.id} isSingleItem />
          ))}
        </SeparatedList>
        <Divider />
      </>
    )
  }

  const renderExtendShippingDeadlineModal = () => {
    return (
      <ExtendShippingDeadlineModal
        isOpen={isExtendShippingDeadlineModalOpen}
        onClose={handleExtendShippingDeadlineModalClose}
      />
    )
  }

  const renderCarrierChangeModal = () => {
    return (
      <CarrierChangeModal
        show={isCarrierChangeModalOpen}
        onClose={handleCarrierChangeModalClose}
        onSubmit={handleCarrierChangeModalSubmit}
      />
    )
  }

  return (
    <>
      <Navigation
        body={
          <Cell styling={Cell.Styling.Tight}>
            <Text
              as="h2"
              text={infoTranslate('details')}
              type={Text.Type.Title}
              theme="amplified"
            />
          </Cell>
        }
        right={
          <Button
            styling={Button.Styling.Flat}
            theme="amplified"
            inline
            iconName={X24}
            onClick={toggleSidebar}
            aria={{
              'aria-label': inboxTranslate('a11y.close'),
            }}
            testId="conversation-info-close-button"
          />
        }
      />
      <Divider />
      <div className="thread__conversation-info">
        {renderOrderItems()}
        {renderModeratedItems()}
        {renderExtendShippingDeadlineAction(!isSellerConversationDetailsGroupOrderActionsAbTestOn)}
        {renderChangeDeliveryOptionAction(!isSellerConversationDetailsGroupOrderActionsAbTestOn)}
        {renderOrderActions()}
        {renderMemberProtectionPolicies()}
        {renderOppositeUser()}
        {renderExtendShippingDeadlineAction(isSellerConversationDetailsGroupOrderActionsAbTestOn)}
        {renderChangeDeliveryOptionAction(isSellerConversationDetailsGroupOrderActionsAbTestOn)}
        {renderConversationActions()}
        <Spacer size={Spacer.Size.Large} />
      </div>
      {renderExtendShippingDeadlineModal()}
      {renderCarrierChangeModal()}
    </>
  )
}

export default ConversationDetails
